import { useState } from "react";
import { endpoints } from "@/lib/api";
import { ShieldAlert, Loader2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { SectionHeader } from "./SovereignPace";

const SCENARIOS = [
    { id: "pre_enlistment", label: "Pre-Enlistment Negotiation" },
    { id: "negotiation", label: "Re-Up / Bonus Negotiation" },
    { id: "exit_interview", label: "Exit Interview Defense" },
];

export const RecruiterShield = () => {
    const [scenario, setScenario] = useState("pre_enlistment");
    const [branch, setBranch] = useState("ARMY");
    const [rank, setRank] = useState("");
    const [context, setContext] = useState(
        "I want a guaranteed MOS in cyber/intel, college fund max, and signing bonus. Recruiter is pushing open contract."
    );
    const [loading, setLoading] = useState(false);
    const [script, setScript] = useState(null);

    const generate = async () => {
        if (!context.trim()) {
            toast.error("Provide context for the scenario");
            return;
        }
        setLoading(true);
        setScript(null);
        try {
            const res = await endpoints.recruiterScript({ scenario, branch, rank, context });
            setScript(res.data);
            toast.success("Script generated · stand fast");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="recruiter-shield"
            data-testid="recruiter-shield"
            className="relative py-20 border-b border-zinc-900 grid-bg"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-8">
                <SectionHeader
                    eyebrow="MODULE · 003"
                    title="The Recruiter Shield"
                    subtitle="Tactical scripts for pre-enlistment negotiation and exit interviews. AI-generated, operator-tested."
                />

                <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-10">
                    <div className="col-span-12 lg:col-span-5 border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 relative corner-bracket">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-gold-300" />
                            <span className="label-tactical">SCENARIO BRIEF</span>
                        </div>

                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="label-tactical">SCENARIO</label>
                                <div className="grid grid-cols-1 gap-2 mt-2">
                                    {SCENARIOS.map((s) => (
                                        <button
                                            key={s.id}
                                            data-testid={`scenario-${s.id}`}
                                            onClick={() => setScenario(s.id)}
                                            className={`text-left px-4 py-3 border transition-all uppercase tracking-wide text-xs font-bold ${
                                                scenario === s.id
                                                    ? "border-gold-300 bg-gold-300/10 text-gold-300"
                                                    : "border-zinc-800 text-zinc-300 hover:border-gold-300/60"
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-tactical">BRANCH</label>
                                    <input
                                        data-testid="shield-branch-input"
                                        value={branch}
                                        onChange={(e) => setBranch(e.target.value.toUpperCase())}
                                        className="mt-2 w-full bg-zinc-900 border border-zinc-800 focus:border-gold-300 px-3 py-2 text-white outline-none uppercase tracking-wider text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="label-tactical">RANK</label>
                                    <input
                                        data-testid="shield-rank-input"
                                        value={rank}
                                        onChange={(e) => setRank(e.target.value)}
                                        placeholder="E-3"
                                        className="mt-2 w-full bg-zinc-900 border border-zinc-800 focus:border-gold-300 px-3 py-2 text-white outline-none uppercase tracking-wider text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label-tactical">CONTEXT · INTEL</label>
                                <textarea
                                    data-testid="shield-context-input"
                                    value={context}
                                    onChange={(e) => setContext(e.target.value)}
                                    rows={5}
                                    className="mt-2 w-full bg-zinc-900 border border-zinc-800 focus:border-gold-300 px-4 py-3 text-white outline-none text-sm leading-relaxed"
                                    placeholder="What does the recruiter want? What do you want? What's the leverage?"
                                />
                            </div>

                            <button
                                data-testid="shield-generate-btn"
                                disabled={loading}
                                onClick={generate}
                                className="w-full bg-gold-300 hover:bg-gold-200 disabled:opacity-60 text-black px-6 py-4 uppercase tracking-[0.22em] text-xs font-black transition-all flex items-center justify-center gap-3"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Drafting...
                                    </>
                                ) : (
                                    <>
                                        <FileText className="w-4 h-4" />
                                        Generate Script
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div
                        data-testid="shield-output"
                        className="col-span-12 lg:col-span-7 border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 relative min-h-[400px]"
                    >
                        <div className="label-tactical">TACTICAL SCRIPT · OUTPUT</div>
                        {!script && !loading && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="text-center max-w-md px-6">
                                    <div className="inline-flex items-center justify-center w-14 h-14 border border-zinc-800 mb-4">
                                        <ShieldAlert className="w-6 h-6 text-zinc-600" />
                                    </div>
                                    <div className="label-tactical">SHIELD · IDLE</div>
                                    <p className="text-zinc-500 text-sm mt-2">
                                        Configure scenario and generate. Outputs include opener, key tactical
                                        lines, leverage points, and red flags.
                                    </p>
                                </div>
                            </div>
                        )}
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <Loader2 className="w-8 h-8 text-gold-300 animate-spin mx-auto" />
                                    <div className="label-tactical mt-4">DRAFTING DOCTRINE</div>
                                </div>
                            </div>
                        )}
                        {script && !loading && (
                            <div className="mt-4 space-y-5">
                                <div>
                                    <div className="label-tactical">{script.scenario?.toUpperCase()}</div>
                                    <h3 className="text-2xl font-black uppercase tracking-tight text-white mt-1">
                                        {script.title}
                                    </h3>
                                </div>

                                {script.opener && (
                                    <ScriptBlock label="OPENER" body={script.opener} />
                                )}

                                {script.key_lines?.length > 0 && (
                                    <div>
                                        <div className="label-tactical">KEY TACTICAL LINES</div>
                                        <ol className="mt-3 space-y-2">
                                            {script.key_lines.map((l, i) => (
                                                <li
                                                    key={i}
                                                    className="border-l-2 border-gold-300 pl-4 py-1 text-zinc-200 text-sm leading-relaxed"
                                                >
                                                    <span className="badge-tier text-gold-300 mr-2">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    {l}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}

                                {script.leverage_points?.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="border border-zinc-800/80 p-4">
                                            <div className="label-tactical">LEVERAGE POINTS</div>
                                            <ul className="mt-3 space-y-1.5">
                                                {script.leverage_points.map((p, i) => (
                                                    <li key={i} className="text-sm text-zinc-300 flex gap-2">
                                                        <span className="text-gold-300">▸</span>
                                                        {p}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="border border-red-900/40 bg-red-950/20 p-4">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                                                <div className="badge-tier text-red-400">RED FLAGS</div>
                                            </div>
                                            <ul className="mt-3 space-y-1.5">
                                                {(script.red_flags || []).map((p, i) => (
                                                    <li key={i} className="text-sm text-zinc-300 flex gap-2">
                                                        <span className="text-red-400">▲</span>
                                                        {p}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                {script.closer && <ScriptBlock label="CLOSER" body={script.closer} />}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const ScriptBlock = ({ label, body }) => (
    <div>
        <div className="label-tactical">{label}</div>
        <p className="text-zinc-300 text-base leading-relaxed mt-2">{body}</p>
    </div>
);

export default RecruiterShield;
